import CONFIG from "config/config";
import { userAuth } from "graphQL/query/userAuth";

// Mirror of oppizi-platform's `createClientByPlatform`
// (src/Containers/Auth/SignUp/graphQL.js). Same mutation name + input shape,
// so the backend resolver in oppizi-api accepts it as-is.
//
// Two relevant quirks of the upstream resolver:
//   1. For email/password signup the resolver returns `authToken: null`
//      (only social signups embed the token). The platform UX handles this
//      by dispatching `userLogin(email, password)` post-signup.
//   2. The resolver wraps the entire create flow — including post-creation
//      side-effects (welcome emails, admin notifications, Slack hooks) — in
//      one big try/catch. If any of those fail (e.g. the email-tracking
//      service in localhost:3300 isn't running), it returns
//      `{ success: false, error }` even though the user/client were already
//      persisted.
//
// We absorb both quirks here: after `createClientFromPlatform` we always
// follow up with `userAuth(email, password)`. If the login succeeds, the
// account exists and we treat the signup as successful regardless of the
// `success` boolean from the create call. The only way to surface a real
// signup failure is when the login itself fails afterwards.

export type CreateClientFromPlatformInput = {
  authMethod: "OPPIZI" | "SOCIAL";
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  role: string;
  mobilePhone: string;
  companyName: string;
  countryId: number | string;
  isSelfServeEnabled?: boolean;
  createdBy?: string;
  providerType?: string;
  providerUserId?: string;
};

export type CreateClientFromPlatformResponse = {
  success: boolean;
  error?: string;
  clientId?: string | number;
  userId?: string | number;
  authToken: string;
  user?: { id: number | string; email: string; role: string; firstName?: string; lastName?: string };
};

const MUTATION = `
  mutation createClientFromPlatform($input: CreateClientFromPlatformInput) {
    createClientFromPlatform(input: $input) {
      success
      error
      clientId
      userId
      authToken
    }
  }
`;

const extractGraphqlErrorMessage = (json: any): string | undefined => {
  const first = Array.isArray(json?.errors) ? json.errors[0] : null;
  if (!first) return undefined;
  if (typeof first.message === "string") return first.message;
  if (typeof first === "string") return first;
  return undefined;
};

const callCreateMutation = async (
  input: CreateClientFromPlatformInput
): Promise<{ data: any; statusOk: boolean; status: number }> => {
  const response = await fetch(CONFIG.GRAPHQL_API_BASE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: MUTATION, variables: { input } }),
  });
  const data = await response.json().catch(() => null);
  return { data, statusOk: response.ok, status: response.status };
};

export const createClientFromPlatform = async (
  input: CreateClientFromPlatformInput
): Promise<CreateClientFromPlatformResponse> => {
  const { data, status } = await callCreateMutation(input);
  const payload = data?.data?.createClientFromPlatform as CreateClientFromPlatformResponse | undefined;

  // OAuth signups already come with a token — short-circuit.
  if (payload?.success && payload.authToken) return payload;

  // For email signups (or local-dev failures), confirm the account by
  // logging in. If the login works, the user definitely exists.
  if (input.password) {
    try {
      const auth = await userAuth(input.email, input.password);
      if (auth?.apiKey) {
        return {
          success: true,
          clientId: payload?.clientId,
          userId: payload?.userId ?? auth.user?.id,
          authToken: auth.apiKey,
          user: auth.user,
        };
      }
    } catch {
      // fall through to surface the original create error
    }
  }

  // Real failure: surface the most informative error available.
  const message = payload?.error || extractGraphqlErrorMessage(data) || `Signup failed (${status})`;
  throw new Error(message);
};
